import { useMemo } from 'react'

import useIsAdmin from '~/auth/hooks/useIsAdmin'
import { useGateValue } from '~/configs/statsig'
import type { ChargerModel, DepotModel } from '~/data/api/client/chargers'
import { type SolarArrayModel, type SynopLinkModel, useGetSynoplinksQuery } from '~/data/api/client/ders'
import type { VehicleModel } from '~/data/api/client/fleet-management'
import { useChargersForCurrentOrg } from '~/data/api/hooks/chargers'
import { useSolarArrays } from '~/data/api/hooks/ders'
import {
  useActualOrgAllowsUserManagment,
  useGetPolledOrgUsers,
  useOrganizationTree
} from '~/data/api/hooks/organizations'
import { useCurrentOrgSites } from '~/data/api/hooks/sites'
import { useVehicles } from '~/data/api/hooks/vehicles'
import type { CustomerWithPath } from '~/data/types/organization'
import type { SynopUser } from '~/data/types/user'
import { useAbility } from '~/hooks/useAuth'
import { useCurrentOrgId } from '~/store/slices/currentOrg'

import type { QuickSearchPage, SearchCategory } from '../types'
import useQuickSearchPages from './useQuickSearchPages'

export type QuickSearchData = {
  siteItems: DepotModel[]
  chargerItems: ChargerModel[]
  vehicleItems: VehicleModel[]
  userItems: SynopUser[]
  organizations: CustomerWithPath[]
  synopLinkItems: SynopLinkModel[]
  solarArrayItems: SolarArrayModel[]
  pages: QuickSearchPage[]
  loadingByCategory: Record<SearchCategory, boolean>
}

export default function useQuickSearchData(): QuickSearchData {
  const isAdmin = useIsAdmin()
  const ability = useAbility()
  const currentOrgId = useCurrentOrgId()
  const canManageUsers = ability.can('update', 'user')
  const orgAllowsUserManagement = useActualOrgAllowsUserManagment()
  const canSeeUsers = canManageUsers && orgAllowsUserManagement

  const { sites, isLoading: sitesLoading } = useCurrentOrgSites({ options: { useCache: true } })

  const { chargers, isLoading: chargersLoading } = useChargersForCurrentOrg(
    { skipEnhanceData: true },
    { useCache: true }
  )
  const chargerItems = useMemo(() => chargers?.items ?? [], [chargers])

  const { vehicles, isLoading: vehiclesLoading } = useVehicles({ options: { useCache: true } })
  const vehicleItems = useMemo(() => vehicles ?? [], [vehicles])

  // Fetch users only when authorized; disable polling to avoid background network traffic.
  const { users, isLoading: usersLoading } = useGetPolledOrgUsers(
    currentOrgId,
    { skipEnhanceData: true },
    { intervalSeconds: 0, skip: !canSeeUsers }
  )
  const userItems = useMemo(() => (canSeeUsers ? (users ?? []) : []), [users, canSeeUsers])

  // Non-admin users should not see organization results or have the ability to cloak into child orgs
  const { organizations = [], isLoading: orgsLoading } = useOrganizationTree({ useCache: true, skip: !isAdmin })

  const { data: synopLinksResponse, isLoading: synopLinksLoading } = useGetSynoplinksQuery(
    { orgId: currentOrgId ? [currentOrgId] : undefined, includeChildren: true, page: 0, count: 250 },
    { skip: !currentOrgId }
  )
  const synopLinkItems = useMemo(
    () => synopLinksResponse?.data ?? [],
    [synopLinksResponse?.data]
  )

  // Solar arrays come from the IndexedDB-backed accessor (org-scoped, gated on
  // solar-in-ui inside the manager), so an ungated org yields an empty list.
  const hasSolarInUi = useGateValue('solar-in-ui')
  const { solarArrays, isLoading: solarArraysLoading } = useSolarArrays()
  // When the gate is off the accessor exposes no arrays, but its isLoading can
  // still flip true from the site-join while cloaking; don't let that show the
  // category as loading.
  const solarArrayLoading = hasSolarInUi && solarArraysLoading

  const pages = useQuickSearchPages()

  const loadingByCategory = useMemo<Record<SearchCategory, boolean>>(() => ({
    site: sitesLoading,
    charger: chargersLoading,
    vehicle: vehiclesLoading,
    user: usersLoading,
    org: orgsLoading,
    synoplink: synopLinksLoading,
    solarArray: solarArrayLoading,
    page: false
  }), [sitesLoading, chargersLoading, vehiclesLoading, usersLoading, orgsLoading, synopLinksLoading, solarArrayLoading])

  return {
    siteItems: sites,
    chargerItems,
    vehicleItems,
    userItems,
    organizations,
    synopLinkItems,
    solarArrayItems: solarArrays,
    pages,
    loadingByCategory
  }
}
